import { CalendarClock, Check, FileText, MessageCircle, MessageSquareText, X, Zap, } from "lucide-react";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { cn } from "../lib/utils";
import { PageCta } from "../components/site/page-cta";
import { CheckoutModal } from "../components/site/checkout-modal";
import usePricingCheckout from "../hooks/usePricingCheckout";
import { SubscriptionHelper } from "view/helpers";

const plans = [
    {
        name: "Free",
        label: "For Individual Owners & Small Landlords",
        monthlyPrice: "$0",
        yearlyPrice: "$0",
        blurb: "Start monitoring a small portfolio with essential alerts and reporting.",
        features: [
            "Up to 5 properties",
            "Property reports for your portfolio",
            "Get alerts on your portfolio",
            "1 seat",
        ],
        excluded: ["Property updates on property changes", "Portfolio support"],
        cta: "Select Plan",
        href: `/sign-up`,
    },
    {
        name: "Starter",
        label: "For Small Property Managers",
        monthlyPrice: "$75",
        yearlyPrice: "$67",
        blurb: "Monitor a growing portfolio with higher alert capacity and team access.",
        features: [
            "Up to 15 properties",
            "Unlimited email alerts and notifications",
            "Get alerts on your portfolio",
            "Stay notified on property changes",
            "1 property report for your portfolio",
            "Email support",
            "5 seats",
        ],
        cta: "Select Plan",
        href: `/sign-up?plan=starter`,
    },
    {
        name: "Pro",
        label: "For Mid-Size Managers & Developers",
        monthlyPrice: "$120",
        yearlyPrice: "$108",
        blurb: "Full-scale monitoring and support for established property teams.",
        features: [
            "Up to 30 properties",
            "Unlimited email alerts and notifications",
            "Get alerts on your portfolio",
            "Stay notified on property changes",
            "2 property reports for your portfolio",
            "Call/email support",
            "10 seats",
        ],
        cta: "Select Plan",
        href: `/sign-up?plan=pro`,
        featured: true,
    },
    {
        name: "Business",
        label: "For Large Portfolios & Regional Firms",
        monthlyPrice: "$199",
        yearlyPrice: "$179",
        blurb: "Unlimited reporting and seats for teams managing larger portfolios.",
        features: [
            "Up to 50 properties",
            "Unlimited email alerts and notifications",
            "Get alerts on your portfolio",
            "Stay notified on property changes",
            "Unlimited property reports for your portfolio",
            "Call/email support",
            "Unlimited seats",
        ],
        cta: "Select Plan",
        href: `/sign-up?plan=business`,
    },
    {
        name: "Enterprise",
        label: "For Institutions & Nationwide Operators",
        monthlyPrice: "Custom Pricing",
        yearlyPrice: "Custom Pricing",
        blurb: "Custom coverage, dedicated management, and priority support at any scale.",
        features: [
            "Custom number of properties",
            "Unlimited email alerts and notifications",
            "Get alerts on your portfolio",
            "Stay notified on property changes",
            "Unlimited property reports for your portfolio",
            "Airtable integration",
            "Dedicated portfolio manager",
            "Unlimited seats",
            "Priority support",
        ],
        cta: "Get a Quote",
        href: "/contact",
    },
];
function Pricing() {
    const checkout = usePricingCheckout();
    const yearly = checkout.isYearly;
    const setYearly = checkout.setIsYearly;
    return (<main className="bg-pricing-background text-pricing-foreground">
      <section className="px-5 pb-20 pt-16 md:pb-24 md:pt-20">
        <div className="mx-auto max-w-[1600px]">
          <header className="mx-auto max-w-2xl text-center">
            <h1 className="text-4xl font-bold md:text-6xl">Plans &amp; Pricing</h1>
            <p className="mt-4 text-base text-pricing-muted md:text-lg">
              No hidden fees, just property-first pricing.
            </p>

            <div className="mx-auto mt-8 inline-flex items-center gap-3 rounded-full border border-pricing-border bg-pricing-card px-5 py-3">
              <span className={cn("text-sm font-bold", yearly ? "text-pricing-muted" : "text-pricing-foreground")}>
                Monthly
              </span>
              <Switch checked={yearly} onCheckedChange={setYearly} aria-label="Toggle yearly pricing"/>
              <span className={cn("text-sm font-bold", yearly ? "text-pricing-foreground" : "text-pricing-muted")}>
                Yearly
              </span>
              <span className="rounded-full bg-pricing-highlight px-3 py-1 text-xs font-bold text-pricing-highlight-foreground">
                SAVE 10%
              </span>
            </div>
          </header>

          <div className="mt-14 grid items-stretch gap-4 md:grid-cols-2 xl:grid-cols-5">
            {plans.map((plan) => (<PricingCard key={plan.name} plan={plan} yearly={yearly} checkout={checkout}/>))}
          </div>

          <SmsAddOn />

          {checkout.checkoutError && (<p className="mt-6 text-center text-sm font-medium text-pricing-danger">
              Error: {checkout.checkoutError?.message || "Something went wrong. Please try again."}
            </p>)}

        </div>
      </section>
      <PageCta />
      <CheckoutModal modal={checkout.modal} isYearly={yearly}/>
    </main>);
}
function PricingCard({ plan, yearly, checkout }) {
    // The design's copy is static; price, button state and checkout come from the live plan
    // (same source the previous pricing table used), matched by plan name.
    const key = plan.name.toLowerCase();
    const livePlan = checkout.finalPlans.find((p) => p.id === key) ?? { id: key, name: plan.name, isCustom: key === "enterprise" };
    const designPrice = yearly ? plan.yearlyPrice : plan.monthlyPrice;
    const price = livePlan.isCustom || livePlan.monthlyPrice === undefined ? designPrice : `$${SubscriptionHelper.getPlanPrice(livePlan, yearly)}`;
    const isCustom = price === "Custom Pricing";
    return (<article className={cn("relative flex flex-col rounded-xl border p-6", plan.featured
            ? "border-pricing-highlight bg-pricing-highlight/20 xl:-my-4 xl:py-10"
            : "border-pricing-border bg-pricing-card")}>
      {plan.featured && (<span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-pricing-highlight px-4 py-1 text-xs font-bold uppercase tracking-wide text-pricing-highlight-foreground">
          Most popular
        </span>)}

      <div>
        <h2 className="text-2xl font-bold">{plan.name}</h2>
        <p className="mt-2 min-h-10 text-sm leading-snug text-pricing-muted">{plan.label}</p>
        <div className="mt-5 flex min-h-14 items-baseline gap-1">
          <span className={cn("font-display font-bold", isCustom ? "text-2xl" : "text-4xl")}>{price}</span>
          {!isCustom && <span className="text-sm text-pricing-muted">/mo</span>}
        </div>
      </div>

      <Button type="button" variant={plan.featured ? "signal" : "outline"} size="lg" onClick={() => checkout.handlePlanSelect(livePlan)} disabled={checkout.getPlanButtonDisabled(livePlan)} className={cn("mt-5 w-full", plan.featured
            ? "bg-pricing-highlight text-pricing-highlight-foreground hover:bg-pricing-highlight/90"
            : "border-pricing-foreground/25 text-pricing-foreground hover:bg-pricing-foreground/5", checkout.isCurrentPlan(livePlan) && "ring-2 ring-pricing-highlight")}>
        {livePlan.name ? checkout.getButtonText(livePlan) : plan.cta}
      </Button>

      <ul className="mt-8 space-y-3">
        {plan.features.map((feature) => (<li key={feature} className="flex gap-2.5 text-sm">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-pricing-foreground" aria-hidden="true"/>
            <span>{feature}</span>
          </li>))}
        {plan.excluded?.map((feature) => (<li key={feature} className="flex gap-2.5 text-sm text-pricing-muted line-through decoration-pricing-danger">
            <X className="mt-0.5 h-4 w-4 shrink-0 text-pricing-danger" aria-hidden="true"/>
            <span>{feature}</span>
          </li>))}
      </ul>
    </article>);
}
const smsFeatures = [
    {
        icon: Zap,
        title: "Instant Urgent Alerts",
        body: "Real-time SMS for Stop Work Orders, Emergency Vacates, and active summonses when Violerts detects them.",
    },
    {
        icon: CalendarClock,
        title: "Hearing & Deadline Reminders",
        body: "OATH & ECB hearing dates and critical DOB certificate deadlines sent directly to your phone.",
    },
    {
        icon: FileText,
        title: "Daily Smart Digest",
        body: "Consolidated daily SMS summary of all standard property updates and filings.",
    },
    {
        icon: MessageCircle,
        title: "Reply to Resolve via SMS",
        body: "Reply directly from your phone to resolve violations.",
    },
];
function SmsAddOn() {
    return (<section className="mx-auto mt-16 max-w-4xl rounded-2xl border border-pricing-border bg-pricing-card p-6 md:p-8">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div className="flex gap-4">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-pricing-highlight/15 text-pricing-foreground">
            <MessageSquareText className="h-5 w-5" aria-hidden="true"/>
          </span>
          <div>
            <h2 className="text-2xl font-bold">SMS Alerts Add On</h2>
            <p className="mt-1 text-sm text-pricing-muted">
              Supercharge any plan with instantaneous SMS text alerts for critical building events.
            </p>
          </div>
        </div>
        <p className="font-display text-3xl font-bold">
          +$19.99<span className="text-sm font-normal text-pricing-muted">/mo</span>
        </p>
      </div>

      <div className="my-7 border-t border-pricing-border"/>

      <div className="grid gap-6 sm:grid-cols-2">
        {smsFeatures.map((feature) => (<div key={feature.title} className="flex gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-pricing-highlight/15 text-pricing-foreground">
              <feature.icon className="h-4 w-4" aria-hidden="true"/>
            </span>
            <div>
              <h3 className="text-sm font-bold">{feature.title}</h3>
              <p className="mt-1 text-sm leading-relaxed text-pricing-muted">{feature.body}</p>
            </div>
          </div>))}
      </div>

      <div className="my-7 border-t border-pricing-border"/>
      <p className="text-sm text-pricing-muted">
        Add to any plan during checkout or enable directly from your dashboard settings.
      </p>
    </section>);
}

export default Pricing;
